import { useMemo, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import { useTheme } from '@/hooks/useTheme'
import {
  useAddMoneyStore,
  PaymentMethodType,
  WalletCurrency,
} from '@/store/useAddMoneyStore'
import { SHOW_APPLE_PAY } from '@/config/app'
import { useAppAnalytics } from '@/services/analytics'

export type DepositTypes =
  | 'crypto-wallet'
  | 'usdt-balance'
  | 'apple-pay'
  | 'google-pay'
  | 'card'
  | 'bank-transfer'

export interface DepositType {
  type: DepositTypes
  iconName: string
  iconSize?: number
  bgIconContainer: [string, string, ...string[]] | string
  title: string
  description: string
  isAvailable: boolean
  flags: string[]
  onPress: () => void
}

interface UseDepositTypesProps {
  isBuy?: boolean
  walletData?: WalletCurrency
}

export const useDepositTypes = ({
  isBuy = false,
  walletData,
}: UseDepositTypesProps): DepositType[] => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const theme = useTheme()
  const { selectedCurrency, setPaymentMethod } = useAddMoneyStore()
  const { trackEvent, events } = useAppAnalytics()

  // Получаем USDT валюту (заглушка для веб-версии)
  const usdtCurrency = useMemo(() => {
    const methods: PaymentMethodType[] = ['card', 'google-pay', 'bank-transfer']
    if (SHOW_APPLE_PAY) {
      methods.splice(1, 0, 'apple-pay') // Вставляем apple-pay после card
    }

    return {
      id: '1',
      name: 'Tether',
      code: 'USDT',
      amount: 100, // Заглушка с балансом
      amountWithoutNumberConversion: '100.00',
      icon: 'https://storage.googleapis.com/kem-catalog-stage/currencies/usdt.png',
      label: '$',
      fiatAmount: 100,
      fiatCurrency: 'USD',
      pricePerUnit: 1,
      ceilingValueForValidation: 0,
      availableOnRampPaymentMethods: methods,
      formattedFiatBalance: '$100.00',
      formattedAmountBalance: '100.00',
      formattedUSDBalance: '$100.00',
      formattedStablecoinBalance: '100.00',
      usdBalance: 100,
      displayCurrencyCode: 'USDT',
      symbol: '$',
      avatarBGRefColor: '#33CC67',
    }
  }, [])

  // Логика для определения доступности и видимости
  const hasBalance = Number(usdtCurrency?.amount || 0) > 0
  const shouldRenderUSDTBalanceDeposit = selectedCurrency && isBuy

  // Получаем доступные методы оплаты из переданной walletData (приоритет) или selectedCurrency
  // walletData содержит актуальные данные о валюте, включая availableOnRampPaymentMethods
  const availableOnRampPaymentMethods =
    walletData?.availableOnRampPaymentMethods ||
      selectedCurrency?.availableOnRampPaymentMethods || [
        'apple-pay',
        'card',
        'bank-transfer',
      ]
  const isCurrencyAvailableForOnRamp = true // TODO: получать из API через featureSettings

  // Функция для редиректа на конверсию
  const redirectToConversionPage = useCallback(() => {
    // TODO: Реализовать переход на страницу конверсии
    navigate('/conversion', {
      state: {
        debitCurrencyCode: 'USDT',
        creditCurrencyCode: selectedCurrency?.code,
      },
    })
  }, [navigate, selectedCurrency])

  // Функция для редиректа на экран добавления денег
  const redirectToAddMoneyScreen = useCallback(
    (paymentMethod: PaymentMethodType) => {
      // Track deposit_started
      trackEvent(events.DEPOSIT_STARTED, {
        entry_point: 'add_money',
        // method: paymentMethod, // Assuming method property is desired, though type check needed if strictly typed
      })

      setPaymentMethod(paymentMethod)

      // Проверяем, поддерживает ли выбранная валюта данный метод оплаты
      const isPaymentMethodSupported =
        selectedCurrency?.availableOnRampPaymentMethods?.includes(paymentMethod)

      // Если метод оплаты не поддерживается валютой, перенаправляем на конверсию
      if (!isPaymentMethodSupported) {
        redirectToConversionPage()
        return
      }

      // Передаем данные валюты через state и cryptoCode через параметры
      navigate('/add-money', {
        state: {
          currency: selectedCurrency,
          cryptoCode: selectedCurrency?.code,
        },
      })
    },
    [setPaymentMethod, navigate, selectedCurrency, redirectToConversionPage],
  )

  // Функция для обработки нажатия на Crypto Wallet
  const handlePressCryptoWallet = useCallback(() => {
    // Если это покупка с балансом USDT - переходим на конверсию
    if (shouldRenderUSDTBalanceDeposit) {
      redirectToConversionPage()
      return
    }

    trackEvent(events.DEPOSIT_STARTED, {
      entry_point: 'add_money',
      // method: 'crypto_wallet', // Manual string
    })

    // Во всех остальных случаях - переходим на страницу выбора валюты
    navigate('/add-money/select-currency')
  }, [shouldRenderUSDTBalanceDeposit, redirectToConversionPage, navigate])

  return useMemo(
    () => [
      {
        type: 'crypto-wallet',
        iconName: 'qrCode',
        bgIconContainer: theme.colors.background.screen,
        iconSize: 32,
        title: t('screens:selectDepositType.cryptoWallet.title'),
        description: t('screens:selectDepositType.cryptoWallet.description'),
        isAvailable: !shouldRenderUSDTBalanceDeposit,
        flags: [
          t('screens:selectDepositType.flags.noFees'),
          t('screens:selectDepositType.flags.noLimits'),
        ],
        onPress: handlePressCryptoWallet,
      },
      {
        type: 'usdt-balance',
        iconName: 'dollar',
        bgIconContainer:
          usdtCurrency?.avatarBGRefColor || theme.colors.background.screen,
        iconSize: 20,
        title: `${usdtCurrency?.code} Balance`,
        description: `${usdtCurrency?.formattedUSDBalance} available`,
        isAvailable: !!shouldRenderUSDTBalanceDeposit && hasBalance,
        flags: [
          t('screens:selectDepositType.flags.fee', { percentage: '1%' }),
          t('screens:selectDepositType.flags.noMinimum'),
        ],
        onPress: handlePressCryptoWallet,
      },
      {
        type: 'apple-pay',
        iconName: 'applePay',
        bgIconContainer: theme.colors.background.screen,
        iconSize: 42,
        title: t('screens:selectDepositType.applePay.title'),
        description: t('screens:selectDepositType.applePay.description'),
        isAvailable:
          SHOW_APPLE_PAY &&
          isCurrencyAvailableForOnRamp &&
          availableOnRampPaymentMethods.includes('apple-pay'),
        flags: [
          t('screens:selectDepositType.flags.fee', { percentage: '3.99%' }),
          t('screens:selectDepositType.flags.upTo', {
            amount: '$5,000',
            period: t('screens:selectDepositType.flags.weekly'),
          }),
        ],
        onPress: () => redirectToAddMoneyScreen('apple-pay'),
      },
      {
        type: 'google-pay',
        iconName: 'googlePay',
        bgIconContainer: theme.colors.background.screen,
        iconSize: 42,
        title: t('screens:selectDepositType.googlePay.title'),
        description: t('screens:selectDepositType.googlePay.description'),
        isAvailable:
          // Проверяем Android (в веб-версии можем определить через user agent)
          navigator.userAgent.includes('Android') &&
          isCurrencyAvailableForOnRamp &&
          availableOnRampPaymentMethods.includes('google-pay'),
        flags: [
          t('screens:selectDepositType.flags.fee', { percentage: '3.99%' }),
          t('screens:selectDepositType.flags.upTo', {
            amount: '$5,000',
            period: t('screens:selectDepositType.flags.weekly'),
          }),
        ],
        onPress: () => redirectToAddMoneyScreen('google-pay'),
      },
      {
        type: 'card',
        iconName: 'cardFilled',
        iconSize: 34,
        bgIconContainer: ['#41FFD1', '#534FFF'],
        title: t('screens:selectDepositType.card.title'),
        description: t('screens:selectDepositType.card.description'),
        isAvailable:
          isCurrencyAvailableForOnRamp &&
          availableOnRampPaymentMethods.includes('card'),
        flags: [
          t('screens:selectDepositType.flags.fee', { percentage: '3.99%' }),
          t('screens:selectDepositType.flags.upTo', {
            amount: '$5,000',
            period: t('screens:selectDepositType.flags.weekly'),
          }),
        ],
        onPress: () => redirectToAddMoneyScreen('card'),
      },
      {
        type: 'bank-transfer',
        iconName: 'bank',
        iconSize: 28,
        bgIconContainer: ['#7866FF', '#4732FF'],
        title: t('screens:selectDepositType.bankTransfer.title'),
        description: t('screens:selectDepositType.bankTransfer.description'),
        // TODO: Временно скрыто - можно быстро вернуть, установив isAvailable на исходное значение
        isAvailable: false,
        flags: [
          `$100 ${t('screens:selectDepositType.flags.minimum')}`,
          t('screens:selectDepositType.flags.upTo', {
            amount: '$250,000',
            period: '',
          }),
        ],
        onPress: () => redirectToAddMoneyScreen('bank-transfer'),
      },
    ],
    [
      t,
      theme,
      selectedCurrency,
      usdtCurrency,
      shouldRenderUSDTBalanceDeposit,
      hasBalance,
      isCurrencyAvailableForOnRamp,
      availableOnRampPaymentMethods,
      handlePressCryptoWallet,
      redirectToAddMoneyScreen,
    ],
  )
}
